import axios from "axios";
import { getAuthErrorMessage, parseApplicantFromApi } from "../auth/authService";
import { getAuthState, setSignedIn } from "../auth/authStore";
import type { Applicant, ProfileUpdatePayload } from "../auth/types";
import { httpClient } from "../http/client";
import { getTryNowJourney } from "../try-now/tryNowJourney";

const STORAGE_APPLICANT_ID = "applicantId";
const STORAGE_ID_TOKEN = "idToken";

interface PostalApiPostOffice {
  Name?: string;
  District?: string;
  State?: string;
}

interface PostalApiItem {
  Status?: string;
  Message?: string;
  PostOffice?: PostalApiPostOffice[] | null;
}

export interface PostalAddressResult {
  pinCode: string;
  postOfficeName: string;
  district: string;
  state: string;
}

export function getApplicantIdFromStorage(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const fromLocal = localStorage.getItem(STORAGE_APPLICANT_ID)?.trim();
  if (fromLocal) {
    return fromLocal;
  }
  const id = getAuthState().applicant?.id;
  return id != null ? String(id) : null;
}

export function getIdTokenFromStorage(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const fromLocal = localStorage.getItem(STORAGE_ID_TOKEN)?.trim();
  if (fromLocal) {
    return fromLocal;
  }
  return getAuthState().firebaseIdToken?.trim() ?? null;
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function setApplicantInStorage(applicant: Applicant): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.setItem("hallticket.applicant.v1", JSON.stringify(applicant));
}

export function mergeApplicantAfterUpdate(current: Applicant, payload: ProfileUpdatePayload): Applicant {
  const dob = payload.dateOfBirth?.trim() || null;
  const cleanedHorizontalReservations = payload.horizontalReservations
    .map((item) => item.trim())
    .filter(Boolean);
  return {
    ...current,
    firstName: payload.firstName,
    lastName: payload.lastName,
    gender: payload.gender || null,
    dateOfBirth: dob ?? current.dateOfBirth,
    category: payload.category || null,
    verticalReservation: payload.verticalReservation || null,
    horizontalReservations: cleanedHorizontalReservations,
    stateOfResidence: payload.stateOfResidence || null,
    stateOfDomicile: payload.stateOfDomicile || null,
    pinCode: payload.pinCode || current.pinCode || null,
    address: payload.address || null,
    city: payload.city || null,
    updatedAt: new Date().toISOString(),
  };
}

/** Maps UI payload to API request body (snake_case fields expected by backend). */
function buildProfileUpdateRequestBody(payload: ProfileUpdatePayload): Record<string, unknown> {
  return {
    ...payload,
    date_of_birth: payload.dateOfBirth,
    state_of_residence: payload.stateOfResidence,
    state_of_domicile: payload.stateOfDomicile,
    pin_code: payload.pinCode,
    vertical_reservation: payload.verticalReservation,
    horizontal_reservations: payload.horizontalReservations,
  };
}

/**
 * PUT /api/v1/applicants/me/profile with Bearer token. Updates session applicant on success.
 */
export async function updateProfile(payload: ProfileUpdatePayload): Promise<void> {
  const tokenBeforeRequest = getAuthState().firebaseIdToken?.trim() ?? null;

  if (!tokenBeforeRequest) {
    throw new Error("No auth token found for your session.");
  }

  const body = buildProfileUpdateRequestBody(payload);

  try {
    const response = await httpClient.put<unknown>("/api/v1/applicants/me/profile", body);

    const current = getAuthState().applicant;
    if (!current) {
      return;
    }

    let next: Applicant = mergeApplicantAfterUpdate(current, payload);

    const responseData = response.data;
    if (responseData !== undefined && responseData !== null && responseData !== "") {
      const data = responseData;
      const direct = parseApplicantFromApi(data);
      if (direct) {
        next = direct;
      } else if (typeof data === "object" && data !== null && "applicant" in data) {
        const nested = parseApplicantFromApi((data as { applicant: unknown }).applicant);
        if (nested) {
          next = nested;
        }
      } else if (typeof data === "object" && data !== null && "data" in data) {
        const nested = parseApplicantFromApi((data as { data: unknown }).data);
        if (nested) {
          next = nested;
        }
      }
    }

    const latestToken = getAuthState().firebaseIdToken?.trim() ?? tokenBeforeRequest;
    setSignedIn(latestToken, next);
    setApplicantInStorage(next);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(getAuthErrorMessage(error));
    }
    throw error;
  }
}

/**
 * Applies pending Try Now selections to the applicant profile after auth.
 * Login does not persist these fields; profile update does.
 */
export async function syncTryNowJourneyToProfile(applicant: Applicant): Promise<void> {
  const journey = getTryNowJourney();
  if (!journey) {
    return;
  }

  const payload: ProfileUpdatePayload = {
    firstName: applicant.firstName ?? "",
    lastName: applicant.lastName ?? "",
    gender: journey.gender,
    dateOfBirth: applicant.dateOfBirth,
    stateOfResidence: applicant.stateOfResidence ?? "",
    stateOfDomicile: applicant.stateOfDomicile ?? applicant.stateOfResidence ?? "",
    pinCode: applicant.pinCode ?? "",
    address: applicant.address ?? "",
    city: applicant.city ?? "",
    category: journey.category,
    verticalReservation: journey.category,
    horizontalReservations: journey.quotas,
  };

  try {
    await updateProfile(payload);
  } catch {
    // Auth should still succeed if Try Now profile sync fails.
  }
}

/**
 * GET latest applicant profile from backend and refreshes auth/local cache.
 */
export async function fetchApplicantProfile(): Promise<Applicant | null> {
  const applicantId = getApplicantIdFromStorage();
  const tokenBeforeRequest = getAuthState().firebaseIdToken?.trim() ?? null;

  if (!applicantId || !tokenBeforeRequest) {
    return null;
  }

  try {
    const response = await httpClient.get<unknown>(`/api/v1/applicants/${encodeURIComponent(applicantId)}`);

    const data = response.data;
    let parsed = parseApplicantFromApi(data);
    if (!parsed && typeof data === "object" && data !== null && "applicant" in data) {
      parsed = parseApplicantFromApi((data as { applicant: unknown }).applicant);
    }
    if (!parsed && typeof data === "object" && data !== null && "data" in data) {
      parsed = parseApplicantFromApi((data as { data: unknown }).data);
    }

    if (!parsed) {
      return null;
    }

    const latestToken = getAuthState().firebaseIdToken?.trim() ?? tokenBeforeRequest;
    setSignedIn(latestToken, parsed);
    setApplicantInStorage(parsed);
    return parsed;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(getAuthErrorMessage(error));
    }
    throw error;
  }
}

/**
 * Fetches address details for an Indian 6-digit PIN code.
 * Uses public postal API: https://api.postalpincode.in
 */
export async function fetchAddressByPinCode(pinCode: string): Promise<PostalAddressResult> {
  const normalizedPin = pinCode.trim();
  if (!/^\d{6}$/.test(normalizedPin)) {
    throw new Error("PIN code must be exactly 6 digits.");
  }

  try {
    const response = await axios.get<PostalApiItem[]>(
      `https://api.postalpincode.in/pincode/${encodeURIComponent(normalizedPin)}`,
      { timeout: 10000 }
    );

    const [first] = response.data ?? [];
    const offices = first?.PostOffice ?? [];
    const office = Array.isArray(offices) ? offices[0] : undefined;

    if (first?.Status !== "Success" || !office) {
      throw new Error(first?.Message?.trim() || "No address found for this PIN code.");
    }

    const postOfficeName = office.Name?.trim() ?? "";
    const district = office.District?.trim() ?? "";
    const state = office.State?.trim() ?? "";

    if (!state) {
      throw new Error("Postal API did not return a state for this PIN code.");
    }

    return {
      pinCode: normalizedPin,
      postOfficeName,
      district,
      state,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const fallback = "Could not fetch address from postal service.";
      const message =
        typeof error.response?.data === "string"
          ? error.response.data
          : error.message || fallback;
      throw new Error(message);
    }
    throw error;
  }
}

/** @deprecated Prefer `updateProfile` — kept for existing imports. */
export const updateApplicantProfile = updateProfile;
